import type { CabinetAppointmentLookup } from "@planerius/contracts/cabinet"
import { useEffect, useState } from "react"

import { fetchAppointmentById } from "../../api/journal-actions.api"
import type { ApiProblem } from "../../api/problem"
import { isApiProblem, transportProblem } from "../../api/problem"

/**
 * Поиск записи ПО НОМЕРУ, когда её нет в загруженном дне (1-Э1.14).
 *
 * ЗАЧЕМ. Ссылку вида `?appointment=<id>` присылают в поддержку, в переписку и позже —
 * из центра уведомлений и разборов сводки. Даты в ней нет, а журнал грузит один день,
 * поэтому вчерашняя запись просто не находилась: панель не открывалась, и экран молчал.
 * Молчание человек читает как «ссылка битая» и идёт искать запись руками.
 *
 * ЧТО ДЕЛАЕТ ХУК. Спрашивает сервер об этой записи и возвращает ЛИБО её день (журнал
 * тут же на него переходит), ЛИБО причину отказа своими словами: чужой филиал, чужая
 * запись, запись удалена. Каждая из них — отдельный ответ человеку, а не общий «нет».
 *
 * ЗАПРОС ИДЁТ ОДИН РАЗ НА НОМЕР. Повторять его на каждый рендер нельзя: это чтение
 * чужого дня, и на медленной связи оно догоняло бы человека, уже ушедшего на другой день.
 */
export interface AppointmentLookup {
  readonly found: CabinetAppointmentLookup | null
  readonly problem: ApiProblem | null
  readonly loading: boolean
}

export function useAppointmentLookup(
  appointmentId: string | undefined,
  enabled: boolean,
): AppointmentLookup {
  const [state, setState] = useState<AppointmentLookup>({
    found: null,
    problem: null,
    loading: false,
  })

  useEffect(() => {
    if (appointmentId === undefined || !enabled) {
      setState({ found: null, problem: null, loading: false })
      return
    }

    const controller = new AbortController()
    setState({ found: null, problem: null, loading: true })

    void (async () => {
      try {
        const found = await fetchAppointmentById(appointmentId, controller.signal)
        if (!controller.signal.aborted) setState({ found, problem: null, loading: false })
      } catch (error) {
        if (controller.signal.aborted) return
        setState({
          found: null,
          problem: isApiProblem(error) ? error : transportProblem("unknown"),
          loading: false,
        })
      }
    })()

    return () => {
      controller.abort()
    }
  }, [appointmentId, enabled])

  return state
}
